import React, { useEffect } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { AppRoutes } from './routes';
import { useGetHealthQuery } from './redux/health/healthApi';
import { useGetSignedInUserQuery } from './redux/auth/authApi';
import { useAppDispatch, useAppSelector } from './hooks/reduxHooks';
import { setFeatures, setLoading, setError } from './redux/health/healthSlice';
import { setAuthState } from './redux/auth/authSlice';
import { getToken } from './helpers/tokenStorage';
import { useIdleTimer } from './hooks/useIdleTimer';

const App: React.FC = () => {
  const dispatch = useAppDispatch();
  const userAuth = useAppSelector(state => state.auth.userAuth);
  const hasStoredToken = Boolean(getToken());
  const shouldRevalidateSession = Boolean(userAuth || hasStoredToken);

  // Initialize idle timer
  useIdleTimer();

  const { data: signedInData, isLoading: isSessionLoading } = useGetSignedInUserQuery(undefined, {
    skip: !shouldRevalidateSession,
    refetchOnMountOrArgChange: true,
  });

  // Fetch health status on app initialization
  const {
    data: healthData,
    isLoading,
    error,
    refetch,
  } = useGetHealthQuery(undefined, {
    pollingInterval: 5 * 60 * 1000,
    refetchOnMountOrArgChange: true,
    refetchOnReconnect: true,
  });

  useEffect(() => {
    if (healthData?.config?.features) {
      dispatch(setFeatures(healthData.config.features));
      dispatch(setLoading(false));
      dispatch(setError(null));
    }
  }, [healthData, dispatch]);

  useEffect(() => {
    if (signedInData?.userAuth) {
      dispatch(setAuthState({ userAuth: signedInData.userAuth }));
    }
  }, [signedInData, dispatch]);

  useEffect(() => {
    if (isLoading) {
      dispatch(setLoading(true));
    }
  }, [isLoading, dispatch]);

  useEffect(() => {
    if (error) {
      dispatch(setError('Failed to fetch feature flags'));
      // Retry after 5 seconds on error
      const retryTimer = setTimeout(() => {
        refetch();
      }, 5000);
      return () => clearTimeout(retryTimer);
    }
  }, [error, dispatch, refetch]);

  if (shouldRevalidateSession && isSessionLoading) {
    return (
      <Box minHeight="100vh" display="flex" alignItems="center" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <AppRoutes />
    </Router>
  );
};

export default App;
